import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import LanguageSelector from "../LanguageSelector";
import logoAtlas from '../../../assets/logo_atlas.png';
import { useTranslation } from 'react-i18next';
import useThemeStore from '../../../stores/useThemeStore';
import { getPortalBranding } from '../portalBranding';

const Footer = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const clientId = useThemeStore(state => state.clientId);
  const isBranded = clientId !== 'default' && clientId !== 'localhost';

  const styles = {
    footer: {
      position: 'relative',
      backgroundColor: theme.palette.background.paper,
      padding: { xs: theme.spacing(1.25, 2), md: theme.spacing(1.25, 3.5) },
      minHeight: 52,
      width: '100%',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: theme.spacing(1.5, 2),
      marginTop: 'auto',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: { xs: theme.spacing(2), md: theme.spacing(3) },
        right: { xs: theme.spacing(2), md: theme.spacing(3) },
        borderTop: `1px solid ${theme.palette.primary.main}`,
      },
    },
    logo: {
      width: 32,
      height: 32,
      order: 3,
      flexShrink: 0,
      backgroundImage: `url(${logoAtlas})`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'left center',
      backgroundSize: 'contain',
      opacity: 1,
    },
    center: {
      order: 1,
      flex: '1 1 180px',
      minWidth: 0,
      display: 'flex',
      justifyContent: 'flex-start',
    },
  };

  if (isBranded) {
    const { clientName } = getPortalBranding(theme, '');

    return (
      <Box component="footer" sx={styles.footer}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '0.75rem', lineHeight: 1.5, flex: { xs: '1 1 100%', sm: '1 1 auto' } }}
        >
          {t('footer.copyright', { year: new Date().getFullYear(), clientName })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          flexWrap: 'wrap', gap: 2, ml: 'auto' }}>
          <LanguageSelector variant="default" />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {t('footer.poweredBy')}
            </Typography>
            <Box role="img" aria-label="Atlas" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="img" src={logoAtlas} alt="" sx={{ width: 28, height: 28, objectFit: 'contain' }} />
              <Typography component="span" sx={{ color: 'text.primary', fontSize: 20, fontWeight: 700,
                letterSpacing: '-0.8px', lineHeight: 1 }}>
                Atlas
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="footer" sx={styles.footer}>
      {/* Logo */}
      <Box sx={styles.logo} />

      {/* Center text */}
      <Box sx={styles.center}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 400, fontSize: '0.75rem', lineHeight: 1.5 }}
        >
          {t('footer.platform')}
        </Typography>
      </Box>

      {/* Language Selector */}
      <Box sx={{ order: 2, marginLeft: 'auto', flexShrink: 0 }}>
        <LanguageSelector variant="default" />
      </Box>
    </Box>
  );
};

export default Footer;
